import { singleton } from 'tsyringe';
import { UsersAdminService } from './UsersAdminService';

/** An assignable/selectable user option (value = email, the stable identifier we persist). */
export interface UserOption {
    value: string;
    label: string;
}

/**
 * Resolves auth (team) users by id — email, display label, and picker options — from one
 * cached load of {@link UsersAdminService.listUsers}. A DI singleton so every view model
 * shares the same fetch. Audit/actor ids and `createdBy`/`updatedBy` are auth user ids;
 * this turns them into something a human can read.
 */
@singleton()
export class UserLookupService {
    private byId = new Map<string, { email: string; displayName: string }>();
    private loadPromise: Promise<void> | null = null;

    constructor(private readonly users: UsersAdminService) {}

    /** Load the user list once (memoized). Safe to await from multiple view models concurrently. */
    ready(): Promise<void> {
        if (this.loadPromise === null) this.loadPromise = this.load();
        return this.loadPromise;
    }

    private async load(): Promise<void> {
        try {
            const list = await this.users.listUsers();
            this.byId = new Map(list.map((u) => [u.id, { email: u.email, displayName: u.displayName }]));
        } catch {
            this.byId = new Map();
        }
    }

    /** Email for a user id, or the id itself when unknown (e.g. 'system'). */
    emailFor(userId: string): string {
        return this.byId.get(userId)?.email ?? userId;
    }

    /** "Display Name · email" for a user id, or the id when unknown. */
    labelFor(userId: string): string {
        const user = this.byId.get(userId);
        if (user === undefined) return userId;
        return user.displayName.length > 0 ? `${user.displayName} · ${user.email}` : user.email;
    }

    /** Options for an assignee/user picker (value = email), sorted by label. */
    options(): UserOption[] {
        return [...this.byId.values()]
            .map((u) => ({ value: u.email, label: u.displayName.length > 0 ? `${u.displayName} · ${u.email}` : u.email }))
            .sort((a, b) => a.label.localeCompare(b.label));
    }
}
